import { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// SortHeader — clickable column header for Operations Deck tables.
// Click toggles Ascending ⇄ Descending; switching columns starts at
// Ascending. Active column shows a directional chevron in accent
// teal; inactive shows a soft up-down hint that brightens on hover.
//
// Used by Companies / Users / Wells / Sites grids.
// ─────────────────────────────────────────────────────────────

export type SortDir = 'Ascending' | 'Descending';

export interface SortHeaderProps {
  /** Display text — same as the static header label it replaces. */
  label: string;
  /** Sort key sent to the backend (or sentinel for client-side cols). */
  col: string;
  /** Current active sort column. */
  activeCol: string;
  /** Current active sort direction. */
  dir: SortDir;
  /** Toggle handler — call `setSortCol(col)` and flip `setSortDir`. */
  onClick: (col: string) => void;
  align?: 'left' | 'right';
}

export function SortHeader({
  label,
  col,
  activeCol,
  dir,
  onClick,
  align = 'left',
}: SortHeaderProps) {
  const active = activeCol === col;
  const [hover, setHover] = useState(false);
  const arrow = active
    ? dir === 'Ascending'
      ? <ChevronUp size={13} />
      : <ChevronDown size={13} />
    : <ChevronsUpDown size={13} style={{ opacity: hover ? 0.85 : 0.35 }} />;

  return (
    <button
      onClick={() => onClick(col)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 600,
        letterSpacing: 0.3,
        color: active ? 'var(--color-accent)' : hover ? 'var(--color-text)' : 'var(--color-text-mid)',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        width: '100%',
        textAlign: align,
        transition: 'color 120ms',
      }}
    >
      <span>{label}</span>
      {arrow}
    </button>
  );
}

/**
 * Helper for hooking SortHeader to a `useCrudPage` instance.
 * Returns a toggler that flips direction on the active column,
 * else switches column and resets to Ascending.
 */
export function makeSortToggle(
  sortCol: string,
  sortDir: SortDir,
  setSortCol: (c: string) => void,
  setSortDir: (d: SortDir) => void,
) {
  return (col: string) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'Ascending' ? 'Descending' : 'Ascending');
    } else {
      setSortCol(col);
      setSortDir('Ascending');
    }
  };
}
